import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api, { getApiUrl } from '../utils/api';

import ChatInterface from '../components/ChatInterface';
import SessionsList from '../components/SessionsList';
import DocumentViewer from '../components/DocumentViewer';
import { isPdfFile, isWordFile } from '../utils/fileUtils';

interface Message {
  role: string;
  content: string;
}

interface UploadedFile {
  file_name: string;
  file_path: string;
  preview_path?: string;
  document_type?: string;
}

interface UploadedImage {
  file_name: string;
  image_path: string;
  image_description?: string;
}

interface Session {
  id: string;
  created_at: string;
  regulation: string;
  file_name?: string;  // Legacy field
  file_path?: string;  // Legacy field
  pdf_path?: string;   // Legacy field
  preview_path?: string;  // Legacy field
  document_type?: string;  // Legacy field
  image_description?: string;  // Legacy field
  image_path?: string;  // Legacy field
  uploaded_files?: UploadedFile[];  // New multi-file support
  uploaded_images?: UploadedImage[];  // New multi-image support
  summary?: string;
  messages: Message[];
  citation_texts?: string[];
  document_quotes?: string[];
  project_name?: string;  // Name of attached project from Vault
  project_id?: string;    // ID of attached project from Vault
}

const SessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<Session | null>(null);
  const [allSessions, setAllSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [documentViewerWidth, setDocumentViewerWidth] = useState(576); // 1.5x wider than original 384px
  const [activeDocumentTab, setActiveDocumentTab] = useState(0); // Active tab index
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Sidebar visibility state
  const hasAutoSentRef = React.useRef(false);
  const isResizing = React.useRef(false);

  // Helper function to get all documents (legacy + new format)
  const getAllDocuments = (session: Session) => {
    const documents: Array<{type: 'file' | 'image', name: string, path: string, data: UploadedFile | UploadedImage | any}> = [];
    
    // Add legacy single file if exists
    if (session.file_path && session.file_name) {
      documents.push({
        type: 'file',
        name: session.file_name,
        path: session.file_path,
        data: {
          file_name: session.file_name,
          file_path: session.file_path,
          preview_path: session.preview_path,
          document_type: session.document_type
        }
      });
    }
    
    // Add legacy single image if exists
    if (session.image_path) {
      documents.push({
        type: 'image',
        name: 'Uploaded Image',
        path: session.image_path,
        data: {
          file_name: 'Uploaded Image',
          image_path: session.image_path,
          image_description: session.image_description
        }
      });
    }
    
    // Add new multiple files
    if (session.uploaded_files) {
      session.uploaded_files.forEach(file => {
        documents.push({
          type: 'file',
          name: file.file_name,
          path: file.file_path,
          data: file
        });
      });
    }
    
    // Add new multiple images
    if (session.uploaded_images) {
      session.uploaded_images.forEach(image => {
        documents.push({
          type: 'image',
          name: image.file_name,
          path: image.image_path,
          data: image
        });
      });
    }
    
    return documents;
  };

  useEffect(() => {
    // Reset auto-send flag when session changes
    hasAutoSentRef.current = false;
    // Reset active tab when session changes
    setActiveDocumentTab(0);
    // Load the specific session and all sessions on component mount
    fetchSession();
    fetchAllSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    // Check if this is a new session with an initial user message but no AI response
    if (session && !isLoading && !hasAutoSentRef.current) {
      // Get only non-system messages
      const nonSystemMessages = session.messages.filter(m => m.role !== 'system');
      
      console.log('Checking for auto-send:', {
        totalMessages: session.messages.length,
        nonSystemMessages: nonSystemMessages.length,
        messages: session.messages.map(m => ({ role: m.role, content: m.content.substring(0, 50) }))
      });
      
      // If we have exactly one user message and no assistant response, trigger AI
      if (nonSystemMessages.length === 1 && nonSystemMessages[0].role === 'user') {
        console.log('Auto-sending initial message');
        hasAutoSentRef.current = true;
        // Add a small delay to ensure the UI is ready
        setTimeout(() => {
          // For initial messages, send empty string to trigger AI response
          // The actual message is already in the session from HomePage
          handleSendMessage('');
        }, 100);
      }
    }
  }, [session, isLoading]);

  const fetchSession = async () => {
    try {
      const response = await api.get(`sessions/${sessionId}`);
      console.log('=== SESSION DATA RECEIVED ===');
      console.log('Session:', response.data);
      console.log('Image path:', response.data.image_path);
      console.log('File path:', response.data.file_path);
      setSession(response.data);
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  const fetchAllSessions = async () => {
    try {
      const response = await api.get('sessions');
      // Sort sessions by created_at date, newest first
      const sortedSessions = [...response.data].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setAllSessions(sortedSessions);
    } catch (error) {
      console.error('Error fetching all sessions:', error);
    }
  };

  const handleSendMessage = async (message: string, imageFile?: File) => {
    if (!session) return;

    let updatedMessages = [...session.messages];
    
    // Only add user message if message is not empty (empty message means it's the initial trigger)
    if (message.trim()) {
      const newUserMessage: Message = { role: 'user', content: message };
      updatedMessages.push(newUserMessage);
    }

    // Add a temporary loading message from Lexa
    const tempLoadingMessage: Message = { role: 'assistant', content: '...' };
    updatedMessages.push(tempLoadingMessage);

    setSession({
      ...session,
      messages: updatedMessages
    });

    setIsLoading(true);

    // Smooth scroll to the loading indicator
    setTimeout(() => {
      const messagesContainer = document.querySelector('.overflow-y-auto');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight - 150;
      }
    }, 100);

    try {
      // If there's an image, send as form data to the image endpoint
      let response;
      if (imageFile) {
        const formData = new FormData();
        formData.append('session_id', sessionId || '');
        formData.append('message', message);
        formData.append('image', imageFile);
        
        response = await api.post('message-with-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await api.post('message', {
          session_id: sessionId,
          message
        });
      }

      // Simulate streaming by gradually revealing the response
      const aiResponse = response.data.messages[response.data.messages.length - 1].content;
      const userMessages = session.messages.filter(m => m.role !== 'assistant' || m.content !== '...');

      // Get document quotes for highlighting if available
      const documentQuotes = response.data.document_quotes || [];

      // Start with an empty response
      let streamedResponse = '';
      const simulateStreaming = (text: string, index: number = 0) => {
        if (index <= text.length) {
          // Add the next character to the response
          streamedResponse = text.substring(0, index);

          // Update the session with the partial response
          // We're not updating session.document_quotes anymore - each message will maintain its own quotes
          setSession({
            ...session,
            messages: [...userMessages, { role: 'assistant', content: streamedResponse }],
            // Store the latest document quotes only for the current response
            document_quotes: index === text.length ? documentQuotes : session.document_quotes
          });

          // Continue streaming with the next character
          if (index < text.length) {
            const delay = 1; // Remove delay, just keep minimal timing for UI updates
            setTimeout(() => simulateStreaming(text, index + 1), delay);
          } else {
            // Finished streaming
            setIsLoading(false);
          }
        }
      };

      // Start the streaming simulation
      simulateStreaming(aiResponse);
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the temporary loading message
      setSession({
        ...session,
        messages: session.messages.filter(m => m.content !== '...')
      });
      setIsLoading(false);
    }
  };

  const handleDeleteSession = async (id: string) => {
    try {
      await api.delete(`sessions/${id}`);
      fetchAllSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isResizing.current = true;
    e.preventDefault();
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      
      const containerRect = document.querySelector('.flex.h-screen')?.getBoundingClientRect();
      if (!containerRect) return;
      
      const newWidth = containerRect.right - e.clientX;
      const minWidth = 300;
      const maxWidth = window.innerWidth * 0.6;
      
      setDocumentViewerWidth(Math.max(minWidth, Math.min(maxWidth, newWidth)));
    };
    
    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Show loading state while fetching session
  if (!session) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-700 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar */}
      {!sidebarCollapsed && (
        <SessionsList
          sessions={allSessions}
          onDeleteSession={handleDeleteSession}
          currentSessionId={sessionId}
          showHeader={false}
          onCollapse={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex relative">
        {/* Sidebar toggle button - only show when sidebar is collapsed */}
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="absolute left-0 top-4 z-10 bg-white border border-gray-200 rounded-r-lg px-2 py-3 hover:bg-gray-50 transition-colors duration-200 shadow-sm"
            title="Show sidebar"
          >
            <svg 
              className="w-4 h-4 text-gray-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {/* >> symbol (chevrons right) */}
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Chat interface */}
        <div className={`flex-1 flex flex-col ${sidebarCollapsed ? 'pl-8' : ''}`}>
          {/* Files/Images/Project indicator */}
          {(() => {
            const allDocuments = getAllDocuments(session);
            const hasContent = allDocuments.length > 0 || session.project_name;
            if (!hasContent) return null;
            
            return (
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                {/* Files/Images info */}
                {allDocuments.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    {allDocuments.length === 1 ? 
                      allDocuments[0].name : 
                      `${allDocuments.length} files/images uploaded`
                    }
                  </div>
                )}
                
                {/* Project info */}
                {session.project_name && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                    </svg>
                    <span className="font-medium">Project: {session.project_name}</span>
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Chat messages area */}
          <div className="flex-1 overflow-hidden">
            <ChatInterface
              messages={session.messages.filter(m => m.role !== 'system' && m.content.trim() !== '')}
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
              documentQuotes={session.document_quotes}
            />
          </div>
        </div>

        {/* Tabbed Document viewer sidebar */}
        {(() => {
          const allDocuments = getAllDocuments(session);
          if (allDocuments.length === 0) return null;
          
          return (
            <div 
              className="border-l border-gray-200 bg-white flex flex-col relative"
              style={{ width: documentViewerWidth }}
            >
              {/* Resize handle */}
              <div
                className="absolute left-0 top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors duration-150 z-10"
                onMouseDown={handleMouseDown}
                style={{ marginLeft: '-2px' }}
              />
              
              {/* Tabs header */}
              <div className="border-b border-gray-200 bg-gray-50">
                <div className="flex overflow-x-auto">
                  {allDocuments.map((doc, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveDocumentTab(index)}
                      className={`flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeDocumentTab === index
                          ? 'border-blue-500 text-blue-600 bg-white'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-2 max-w-32">
                        {doc.type === 'image' ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                        )}
                        <span className="truncate">{doc.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab content */}
              <div className="flex-1 overflow-auto">
                {allDocuments[activeDocumentTab] && (() => {
                  const activeDoc = allDocuments[activeDocumentTab];
                  
                  if (activeDoc.type === 'image') {
                    return (
                      <div className="h-full flex items-center justify-center p-4">
                        <img 
                          src={getApiUrl(`documents/${encodeURIComponent(activeDoc.path)}`)}
                          alt={activeDoc.name}
                          className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                          style={{ maxHeight: 'calc(100vh - 200px)' }}
                        />
                      </div>
                    );
                  } else {
                    // File document
                    if (isPdfFile(activeDoc.path) || isWordFile(activeDoc.path)) {
                      return (
                        <DocumentViewer
                          filePath={activeDoc.path}
                          fileName={activeDoc.name}
                          documentQuotes={session.document_quotes}
                        />
                      );
                    } else {
                      return (
                        <div className="flex items-center justify-center h-full">
                          <div className="p-6 text-center">
                            <p className="text-gray-500">Preview not available</p>
                            <p className="text-sm text-gray-400 mt-2">{activeDoc.name}</p>
                            <a
                              href={getApiUrl(`documents/${activeDoc.path.split('/').pop()}`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-4 bg-gray-200 hover:bg-gray-300 px-3 py-1.5 rounded-md text-sm"
                            >
                              Download File
                            </a>
                          </div>
                        </div>
                      );
                    }
                  }
                })()}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SessionPage;